// Gantt Sidebar Component - Task list and filters
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, ChevronRight, ChevronDown, MoreVertical,
  Calendar, User, Flag, Circle, CheckCircle, AlertCircle,
  Clock, Folder, Filter, X, Hash, Briefcase
} from 'lucide-react';
import { Task, Project, GanttFilters, TaskStatus, TaskPriority } from '../types/gantt.types';

interface GanttSidebarProps {
  tasks: Task[];
  projects: Project[];
  selectedProject: string | null;
  onProjectSelect: (projectId: string | null) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onTaskClick: (task: Task) => void;
  onTaskCreate: () => void;
  filters: GanttFilters;
  onFiltersChange: (filters: GanttFilters) => void;
  showFilters: boolean;
}

export const GanttSidebar: React.FC<GanttSidebarProps> = ({
  tasks,
  projects,
  selectedProject,
  onProjectSelect,
  searchQuery,
  onSearchChange,
  onTaskClick,
  onTaskCreate,
  filters,
  onFiltersChange,
  showFilters
}) => {
  const [expandedProjects, setExpandedProjects] = useState<string[]>(
    projects.map(p => p.id)
  );
  const [expandedTasks, setExpandedTasks] = useState<string[]>([]);

  // Filter tasks based on search and filters
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Search filter
      if (searchQuery && !task.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Project filter
      if (selectedProject && task.project_id !== selectedProject) {
        return false;
      }

      // Status filter
      if (filters.status && filters.status.length > 0 && !filters.status.includes(task.status)) {
        return false;
      }

      // Priority filter
      if (filters.priority && filters.priority.length > 0 && !filters.priority.includes(task.priority)) {
        return false;
      }

      // Assigned filter
      if (filters.assigned_to && filters.assigned_to.length > 0) {
        const taskAssignees = task.assigned_to || [];
        if (!filters.assigned_to.some(userId => taskAssignees.includes(userId))) {
          return false;
        }
      }

      // Show completed filter
      if (!filters.show_completed && task.status === 'completed') {
        return false;
      }

      // Show cancelled filter
      if (!filters.show_cancelled && task.status === 'cancelled') {
        return false;
      }

      return true;
    });
  }, [tasks, searchQuery, selectedProject, filters]);

  // Group tasks by project
  const tasksByProject = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    filteredTasks.forEach(task => {
      if (!grouped[task.project_id]) {
        grouped[task.project_id] = [];
      }
      grouped[task.project_id].push(task);
    });
    return grouped;
  }, [filteredTasks]);

  // Toggle project expansion
  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  // Toggle task expansion (for subtasks)
  const toggleTask = (taskId: string) => {
    setExpandedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  // Get status icon
  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'in_progress':
        return <Circle className="w-4 h-4 text-blue-400 animate-pulse" />;
      case 'delayed':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'cancelled':
        return <X className="w-4 h-4 text-gray-400" />;
      default:
        return <Circle className="w-4 h-4 text-gray-400" />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'critical':
        return 'text-red-400';
      case 'high':
        return 'text-orange-400';
      case 'medium':
        return 'text-yellow-400';
      case 'low':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  // Render task item
  const renderTask = (task: Task, level: number = 0) => {
    const hasSubtasks = task.subtasks && task.subtasks.length > 0;
    const isExpanded = expandedTasks.includes(task.id);

    return (
      <div key={task.id} className="task-item">
        <div
          className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-800 cursor-pointer transition-colors group"
          style={{ paddingLeft: `${12 + level * 20}px` }}
          onClick={() => onTaskClick(task)}
        >
          {/* Expand/Collapse for subtasks */}
          {hasSubtasks ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleTask(task.id);
              }}
              className="p-0.5 hover:bg-zinc-700 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          ) : (
            <div className="w-4" />
          )}

          {/* Status icon */}
          {getStatusIcon(task.status)}

          {/* Task name */}
          <span className="flex-1 text-sm text-gray-300 truncate">
            {task.name}
          </span>

          {/* Task info */}
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Priority */}
            <Flag className={`w-3 h-3 ${getPriorityColor(task.priority)}`} />
            
            {/* Progress */}
            <span className="text-xs text-gray-500">{task.progress}%</span>
            
            {/* More options */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Show context menu
              }}
              className="p-0.5 hover:bg-zinc-700 rounded transition-colors"
            >
              <MoreVertical className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Render subtasks if expanded */}
        {hasSubtasks && isExpanded && (
          <div className="subtasks">
            {task.subtasks!.map(subtask => renderTask(subtask, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-80 bg-zinc-900 border-r border-zinc-800 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white">Zadania</h3>
          <button
            onClick={onTaskCreate}
            className="p-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            title="Nowe zadanie"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Szukaj zadań..."
            className="w-full pl-10 pr-3 py-2 bg-zinc-800 text-white rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-b border-zinc-800 overflow-hidden"
          >
            <div className="p-4 space-y-3">
              {/* Status Filter */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider">Status</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {(['planned', 'in_progress', 'completed', 'delayed', 'cancelled'] as TaskStatus[]).map(status => (
                    <button
                      key={status}
                      onClick={() => {
                        const newStatus = filters.status || [];
                        if (newStatus.includes(status)) {
                          onFiltersChange({
                            ...filters,
                            status: newStatus.filter(s => s !== status)
                          });
                        } else {
                          onFiltersChange({
                            ...filters,
                            status: [...newStatus, status]
                          });
                        }
                      }}
                      className={`px-2 py-1 text-xs rounded-lg transition-all ${
                        filters.status?.includes(status)
                          ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                          : 'bg-zinc-800 text-gray-400 hover:text-white'
                      }`}
                    >
                      {status.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider">Priorytet</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {(['low', 'medium', 'high', 'critical'] as TaskPriority[]).map(priority => (
                    <button
                      key={priority}
                      onClick={() => {
                        const newPriority = filters.priority || [];
                        if (newPriority.includes(priority)) {
                          onFiltersChange({
                            ...filters,
                            priority: newPriority.filter(p => p !== priority)
                          });
                        } else {
                          onFiltersChange({
                            ...filters,
                            priority: [...newPriority, priority]
                          });
                        }
                      }}
                      className={`px-2 py-1 text-xs rounded-lg transition-all ${
                        filters.priority?.includes(priority)
                          ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                          : 'bg-zinc-800 text-gray-400 hover:text-white'
                      }`}
                    >
                      {priority}
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggle Filters */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.show_completed !== false}
                    onChange={(e) => onFiltersChange({
                      ...filters,
                      show_completed: e.target.checked
                    })}
                    className="rounded border-gray-600 bg-zinc-800 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-400">Pokaż ukończone</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.show_cancelled === true}
                    onChange={(e) => onFiltersChange({
                      ...filters,
                      show_cancelled: e.target.checked
                    })}
                    className="rounded border-gray-600 bg-zinc-800 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-400">Pokaż anulowane</span>
                </label>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Project Selector */}
      <div className="p-3 border-b border-zinc-800">
        <select
          value={selectedProject || ''}
          onChange={(e) => onProjectSelect(e.target.value || null)}
          className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="">Wszystkie projekty</option>
          {projects.map(project => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      {/* Task List */}
      <div className="flex-1 overflow-y-auto">
        {projects.map(project => {
          const projectTasks = tasksByProject[project.id] || [];
          if (projectTasks.length === 0 && selectedProject && selectedProject !== project.id) {
            return null;
          }

          const isExpanded = expandedProjects.includes(project.id);

          return (
            <div key={project.id} className="project-group">
              {/* Project Header */}
              <div
                className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 hover:bg-zinc-800 cursor-pointer sticky top-0 z-10"
                onClick={() => toggleProject(project.id)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleProject(project.id);
                  }}
                  className="p-0.5 hover:bg-zinc-700 rounded transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                <Folder className="w-4 h-4 text-orange-400" />
                <span className="flex-1 text-sm font-medium text-white">
                  {project.name}
                </span>
                <span className="text-xs text-gray-500">
                  {projectTasks.length}
                </span>
              </div>

              {/* Project Tasks */}
              {isExpanded && (
                <div className="tasks">
                  {projectTasks.length > 0 ? (
                    projectTasks.map(task => renderTask(task))
                  ) : (
                    <div className="px-4 py-3 text-center text-gray-500 text-sm">
                      Brak zadań w tym projekcie
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Empty state */}
        {filteredTasks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Briefcase className="w-12 h-12 text-gray-600 mb-3" />
            <p className="text-gray-400 text-sm">Brak zadań do wyświetlenia</p>
            <button
              onClick={onTaskCreate}
              className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
            >
              Utwórz pierwsze zadanie
            </button>
          </div>
        )}
      </div>
    </div>
  );
};